import type {
  GetApiV1ThreeLetterCodePartnerAdminsBankAccounts200One,
  GetApiV1ThreeLetterCodePartnerAdminsUsersUserId200,
  GetApiV1ThreeLetterCodePartnerAdminsUsersUserIdProfile200,
} from '@fukuri-admins/apis/client/gen/model';
import { formatDate } from '@fukuri-admins/utils/common/date/formatDate';
import { getUserEditLink } from '~/utils/getLinks';
import { UserDetailProfileBankAccountPresentation } from '../presentations';
import { convertCustomFieldsToDisplayItems } from './convertCustomFieldsToDisplayItems';
import { selectLatestBankAccount } from './selectLatestBankAccount';

export { convertCustomFieldsToDisplayItems, selectLatestBankAccount };

type PresentationProps = React.ComponentProps<typeof UserDetailProfileBankAccountPresentation>;

type Args = {
  /** 利用者詳細 API のレスポンスデータ */
  data: GetApiV1ThreeLetterCodePartnerAdminsUsersUserId200;
  /** 利用者ID */
  userId: number;
  /** プロフィール API のレスポンスデータ */
  profileData: GetApiV1ThreeLetterCodePartnerAdminsUsersUserIdProfile200;
  /**
   * 口座情報 API のレスポンスデータ。
   * 未連携ユーザー・依存フェッチ未発火時は undefined。
   */
  bankAccountData: GetApiV1ThreeLetterCodePartnerAdminsBankAccounts200One | undefined;
};

type IConvertToPresentationProps = (args: Args) => PresentationProps;

/**
 * 口座番号をマスク表示用の文字列に変換する
 *
 * Figma 仕様: ` ••••••••{last4}` 形式
 */
const formatAccountNumber = (accountNumberLast4: string): string =>
  ` ••••••••${accountNumberLast4}`;

/**
 * API レスポンスを UserDetailProfileBankAccountPresentation の props に変換する
 */
export const convertToPresentationProps: IConvertToPresentationProps = ({
  data,
  userId,
  profileData,
  bankAccountData,
}) => {
  const { user } = data;
  const latestBankAccount = selectLatestBankAccount(bankAccountData?.bank_accounts ?? []);

  const accountNumber =
    latestBankAccount != null ? formatAccountNumber(latestBankAccount.account_number_last4) : '-';

  const linkedAtLabel =
    latestBankAccount != null
      ? formatDate({
          date: new Date(latestBankAccount.linked_at * 1000),
          formatType: 'yyyy/MM/dd H:mm',
        })
      : '-';

  const billableFromLabel = user.billable_from
    ? formatDate({
        date: new Date(`${user.billable_from}T00:00:00`),
        formatType: 'yyyy/M/d',
      })
    : '-';

  return {
    fullName: user.full_name,
    email: user.email ?? '-',
    clientName: user.client.name,
    // 店番は BE レスポンス未対応のため固定
    branchCode: '-',
    accountNumber,
    linkedAtLabel,
    billableFromLabel,
    createdAtLabel: formatDate({
      date: new Date(user.created_at * 1000),
      formatType: 'yyyy/MM/dd H:mm',
    }),
    updatedAtLabel: formatDate({
      date: new Date(user.updated_at * 1000),
      formatType: 'yyyy/MM/dd H:mm',
    }),
    editLink: getUserEditLink(String(userId)),
    customFields: convertCustomFieldsToDisplayItems(
      profileData.user_profile.custom_field_values ?? []
    ),
  };
};
